import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  getListNextActionFeedQueryKey,
  getListNextActionFeedQueryOptions,
  useApproveNextAction,
  useDismissNextAction,
  useListNextActionFeed,
  useRecordRelationshipHealthFeedback,
  useRefreshNextActions,
  useSnoozeNextAction,
  type ListNextActionFeedParams,
  type ListNextActionFeedQueryResult,
  type NextActionFeedItem
} from '@cdf/api-client-react';
import {
  CheckCircle2Icon,
  ClipboardCheckIcon,
  ClockIcon,
  HandshakeIcon,
  MailIcon,
  MessageSquareTextIcon,
  PhoneCallIcon,
  RefreshCwIcon,
  Share2Icon,
  XIcon,
  type LucideIcon
} from 'lucide-react';

import { SuggestionExplanation } from './suggestion-explanation';
import { useGuardedNavigate } from '@/hooks/use-draft-navigation-guard';
import { ListPagination } from '@/components/interaction/query-feedback';
import { lastPageOffset } from '@/components/interaction/interaction-scope';
import { DraftReviewSheet } from '@/components/next-actions/draft-review-sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, getErrorMessage } from '@/lib/utils';

type ActionFeedItem = ListNextActionFeedQueryResult['items'][number];

const ACTION_TYPE_META: Record<NextActionFeedItem['actionType'], { icon: LucideIcon; label: string }> = {
  email: { icon: MailIcon, label: 'Email' },
  sms: { icon: MessageSquareTextIcon, label: 'SMS' },
  social: { icon: Share2Icon, label: 'Social' },
  call: { icon: PhoneCallIcon, label: 'Call' },
  review_signal: { icon: ClipboardCheckIcon, label: 'Review' },
  create_deal: { icon: HandshakeIcon, label: 'Opportunity' }
};

export const AUDIENCE_LABELS: Record<NextActionFeedItem['audience'], string> = {
  b2b: 'B2B',
  b2c: 'B2C',
  partner: 'Partner',
  investor: 'Investor'
};

const SNOOZE_OPTIONS = [
  { label: 'Until tomorrow', hours: 24 },
  { label: 'For 3 days', hours: 72 },
  { label: 'For a week', hours: 168 }
];

function formatAge(iso: string): string {
  const minutes = Math.floor((Date.now() - Date.parse(iso)) / 60_000);
  if (Number.isNaN(minutes) || minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function priorityLabel(priority: number): string {
  if (priority >= 80) return 'Urgent';
  if (priority >= 60) return 'High';
  if (priority >= 40) return 'Medium';
  return 'Low';
}

export function ActionCard({
  action,
  onDismiss,
  onSnooze,
  onApprove,
  busy,
  showAudienceBadge = false,
  selected = false,
  suggestions = false,
  scopeLabel,
  healthLink
}: {
  action: ActionFeedItem;
  onDismiss: (id: string) => void;
  onSnooze: (id: string, hours: number) => void;
  onApprove: (action: ActionFeedItem) => void;
  busy: boolean;
  showAudienceBadge?: boolean;
  selected?: boolean;
  healthLink?: string;
  suggestions?: boolean;
  scopeLabel?: string;
}) {
  const meta = ACTION_TYPE_META[action.actionType];
  const Icon = meta.icon;
  const entityName = action.contactName ?? action.companyName ?? null;
  const hasDraft = (action.actionType === 'email' || action.actionType === 'sms') && Boolean(action.draft);

  return (
    <Card id={`next-action-${action.id}`} aria-current={selected ? 'true' : undefined} className={selected ? 'ring-ring ring-2' : undefined}>
      <CardContent className="flex flex-col gap-3 p-4">
        <div className="flex items-start gap-3">
          <div className="bg-muted text-muted-foreground flex size-10 shrink-0 items-center justify-center rounded-md border">
            <Icon className="size-5" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={action.priority >= 80 ? 'default' : 'secondary'}>
                {priorityLabel(action.priority)}
              </Badge>
              {showAudienceBadge && (
                <Badge variant="outline">{AUDIENCE_LABELS[action.audience]}</Badge>
              )}
              <span className="text-muted-foreground text-xs">{meta.label}</span>
              {action.draft && (
                <Badge variant="outline" className="gap-1">
                  <MailIcon className="size-3" aria-hidden /> Draft ready
                </Badge>
              )}
              {selected && <Badge variant="secondary">Opened from link</Badge>}
              {(action.priorityReasons ?? []).map((reason) => (
                <Badge key={reason} variant="outline" data-ui="rank-reason">{reason}</Badge>
              ))}
            </div>
            <h3 className="mt-1.5 leading-snug font-semibold break-words">
              {healthLink && !suggestions ? <Link className="hover:underline" to={healthLink}>{action.title}</Link> : action.title}
            </h3>
            <p className="text-muted-foreground mt-1 text-sm leading-snug break-words">
              {action.rationale}
            </p>
          </div>
        </div>

        <div className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          {entityName && !suggestions && action.deepLink && (
            <Link
              to={action.deepLink}
              className="hover:text-foreground underline decoration-dotted underline-offset-2"
            >
              {entityName}
            </Link>
          )}
          <span>{formatAge(action.createdAt)}</span>
        </div>

        <SuggestionExplanation action={action} scopeLabel={scopeLabel} />
        <div className="flex flex-wrap gap-2">
          <Button
            className="h-11 min-w-0 flex-1 sm:flex-none sm:px-6"
            disabled={busy}
            onClick={() => onApprove(action)}
          >
            {suggestions ? 'Open review' : hasDraft ? 'Review & approve' : 'Approve'}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="h-11 min-w-0 flex-1 sm:flex-none sm:px-5" disabled={busy}>
                <ClockIcon className="size-4" aria-hidden />
                Snooze
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {SNOOZE_OPTIONS.map((option) => (
                <DropdownMenuItem key={option.hours} onClick={() => onSnooze(action.id, option.hours)}>
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="ghost"
            className="h-11 min-w-0 flex-1 sm:flex-none sm:px-5"
            disabled={busy}
            onClick={() => onDismiss(action.id)}
          >
            <XIcon className="size-4" aria-hidden />
            Dismiss
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function ActionFeedSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="flex flex-col gap-3">
      {Array.from({ length: count }, (_, index) => (
        <Card key={index}>
          <CardContent className="flex flex-col gap-3 p-4">
            <div className="flex items-start gap-3">
              <Skeleton className="size-10 shrink-0 rounded-md" />
              <div className="flex w-full flex-col gap-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-4 w-full" />
              </div>
            </div>
            <Skeleton className="h-11 w-full" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function NextActionsFeed({
  audience,
  accountId,
  selectedActionId,
  linkToHealth = false,
  limit,
  showAudienceBadges = false,
  className,
  suggestions = false,
  scopeLabel,
  boundary = '',
  disabled = false,
  emptyDescription = 'No open actions right now. Refresh to run the intelligence engine again.'
}: {
  audience?: NextActionFeedItem['audience'];
  accountId?: string;
  selectedActionId?: string;
  linkToHealth?: boolean;
  limit?: number;
  showAudienceBadges?: boolean;
  className?: string;
  suggestions?: boolean;
  scopeLabel?: string;
  disabled?: boolean;
  boundary?: string;
  emptyDescription?: string;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const guardedNavigate = useGuardedNavigate();
  const [offset, setOffset] = useState(0);
  const pageSize = limit ?? 20;
  const heading = useRef<HTMLDivElement>(null);
  const alive = useRef(true), selecting = useRef(false), blocked = useRef(disabled);
  blocked.current = disabled;
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const [opening, setOpening] = useState(false);
  const feedParams: ListNextActionFeedParams = {
    ...(audience ? { audience } : {}),
    ...(accountId ? { accountId } : {}),
    limit: pageSize, offset,
    ...(selectedActionId ? { actionId: selectedActionId } : {})
  };
  const feedKey = [...getListNextActionFeedQueryKey(feedParams), ...(boundary ? [boundary] : [])];
  const query = useListNextActionFeed(feedParams, {
    query: {
      queryKey: feedKey,
      refetchOnWindowFocus: true,
      refetchInterval: suggestions ? false : 60_000,
      retry: false,
      staleTime: 30_000
    }
  });
  useEffect(() => { setOffset(0); }, [audience, accountId, selectedActionId]);
  useEffect(() => {
    if (query.data && !query.isFetching && !query.isError && offset > lastPageOffset(query.data.total, pageSize)) setOffset(lastPageOffset(query.data.total, pageSize));
  }, [query.data, query.isFetching, query.isError, offset, pageSize]);
  const [refreshMessage, setRefreshMessage] = useState<string | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState<ActionFeedItem | null>(null);
  const [sheetError, setSheetError] = useState<string | null>(null);
  const [humanOnlyNotice, setHumanOnlyNotice] = useState<string | null>(null);
  const [feedbackNotice, setFeedbackNotice] = useState<string | null>(null);
  const executionRequestIds = useRef(new Map<string, string>());
  const feedbackRequestIds = useRef(new Map<string, string>());

  const executionRequestId = (actionId: string) => {
    const existing = executionRequestIds.current.get(actionId);
    if (existing) return existing;
    const created = crypto.randomUUID();
    executionRequestIds.current.set(actionId, created);
    return created;
  };

  const feedback = useRecordRelationshipHealthFeedback({
    mutation: {
      onError: () => {
        setFeedbackNotice('The action was updated, but its review feedback could not be recorded. Refresh before trying again.');
      }
    }
  });

  const recordFeedback = (
    actionId: string,
    feedbackType: 'approval' | 'edit' | 'rejection' | 'deferral' | 'handoff',
    outcome: 'accepted' | 'edited' | 'rejected' | 'deferred' | 'staff_handoff'
  ) => {
    const key = `${actionId}:${feedbackType}:${outcome}`;
    const requestId = feedbackRequestIds.current.get(key) ?? crypto.randomUUID();
    feedbackRequestIds.current.set(key, requestId);
    feedback.mutate({ data: { requestId, nextActionId: actionId, feedbackType, outcome } });
  };

  const removeFromFeed = (id: string): ListNextActionFeedQueryResult | undefined => {
    const previous = queryClient.getQueryData<ListNextActionFeedQueryResult>(feedKey);
    queryClient.setQueryData<ListNextActionFeedQueryResult>(feedKey, (current) =>
      current ? { ...current, items: current.items.filter((item) => item.id !== id) } : current
    );
    return previous;
  };

  const restoreFeed = (previous: ListNextActionFeedQueryResult | undefined) => {
    if (previous) queryClient.setQueryData(feedKey, previous);
  };

  const dismiss = useDismissNextAction({
    mutation: {
      onMutate: ({ id }) => removeFromFeed(id),
      onSuccess: (_result, variables) => {
        if (!alive.current) return;
        recordFeedback(variables.id, 'rejection', 'rejected');
        void queryClient.invalidateQueries({ queryKey: getListNextActionFeedQueryKey() });
      },
      onError: (error, _variables, context) => {
        restoreFeed(context);
        setMutationError(`Dismiss failed: ${getErrorMessage(error)}`);
      }
    }
  });

  const snooze = useSnoozeNextAction({
    mutation: {
      onMutate: ({ id }) => removeFromFeed(id),
      onSuccess: (_result, variables) => {
        if (!alive.current) return;
        recordFeedback(variables.id, 'deferral', 'deferred');
        void queryClient.invalidateQueries({ queryKey: getListNextActionFeedQueryKey() });
      },
      onError: (error, _variables, context) => {
        restoreFeed(context);
        setMutationError(`Snooze failed: ${getErrorMessage(error)}`);
      }
    }
  });

  const approve = useApproveNextAction({
    mutation: {
      onMutate: ({ id }) => removeFromFeed(id),
      onSuccess: (result, variables, context) => {
        setSheetError(null);
        if (result.outcome === 'human_only') {
          restoreFeed(context);
          recordFeedback(result.action.id, 'handoff', 'staff_handoff');
          setHumanOnlyNotice(result.message ?? 'Automated sending is blocked for this action type.');
          return;
        }
        const approvedDraft = variables.data;
        const edited = Boolean(
          reviewing?.id === result.action.id &&
          approvedDraft &&
          (approvedDraft?.subject !== reviewing.draft?.subject || approvedDraft?.body !== reviewing.draft?.body)
        );
        recordFeedback(result.action.id, edited ? 'edit' : 'approval', edited ? 'edited' : 'accepted');
        setHumanOnlyNotice(null);
        executionRequestIds.current.delete(result.action.id);
        setReviewing(null);
        if (result.outcome === 'sent') {
          setMutationError(null);
          setRefreshMessage(`Email sent to ${result.sentTo ?? 'recipient'}.`);
        } else {
          setRefreshMessage(null);
          if (result.action.deepLink) navigate(result.action.deepLink);
        }
        void queryClient.invalidateQueries({ queryKey: getListNextActionFeedQueryKey() });
      },
      onError: (error, _variables, context) => {
        restoreFeed(context);
        const message = (error as { status?: number }).status === 409
          ? 'This recommendation changed while it was open. Refresh the queue and review the latest version.'
          : `Approve failed: ${getErrorMessage(error)}`;
        if (reviewing) setSheetError(message);
        else setMutationError(message);
      }
    }
  });

  const refresh = useRefreshNextActions({
    mutation: {
      onSuccess: (result) => {
        setRefreshError(null);
        setRefreshMessage(
          `${result.signalsConsidered} signal${result.signalsConsidered === 1 ? '' : 's'} considered — ` +
            `${result.created} new, ${result.updated} updated` +
            (result.closed > 0 ? `, ${result.closed} resolved` : '') +
            (result.llm === 'fallback' ? ' (AI copy unavailable, using fallback text)' : '')
        );
        void queryClient.invalidateQueries({ queryKey: getListNextActionFeedQueryKey() });
      },
      onError: (error) => {
        setRefreshMessage(null);
        setRefreshError(getErrorMessage(error));
      }
    }
  });

  const busy = disabled || opening || dismiss.isPending || snooze.isPending || approve.isPending;
  const refreshing = refresh.isPending;
  const items = [...new Map((query.data?.items ?? []).map(item => [item.id, item])).values()];
  async function openReview(item: ActionFeedItem) {
    if (selecting.current || blocked.current) return;
    selecting.current = true; setOpening(true); setMutationError(null);
    try {
      const params = { ...(audience ? { audience } : {}), ...(accountId ? { accountId } : {}), actionId: item.id, limit: 1 };
      const options = getListNextActionFeedQueryOptions(params);
      const fresh = await queryClient.fetchQuery({ ...options, queryKey: [...options.queryKey, boundary], retry: false, staleTime: 0, gcTime: 0 });
      if (!alive.current || blocked.current) return;
      const current = fresh.items.find(value => value.id === item.id);
      if (!current) { setMutationError('This suggestion is resolved, snoozed, dismissed or no longer available. Reload the list. Nothing was performed.'); return; }
      if (current.updatedAt !== item.updatedAt) { setMutationError('This suggestion changed. Reload and inspect the latest reason before opening review.'); return; }
      const opened = guardedNavigate('/relationship-health?' + new URLSearchParams({ view: 'actions', action: item.id, ...(item.accountId ? { accountId: item.accountId } : {}) }).toString());
      if (!opened) setMutationError('Keep or finish your current draft before opening review. Nothing was performed.');
    } catch { if (alive.current) setMutationError('This suggestion could not be checked. Access or its source may be unavailable. Retry the read; nothing was performed.'); }
    finally { if (alive.current) { selecting.current = false; setOpening(false); } }
  }
  const notice = refreshError ?? mutationError ?? refreshMessage;
  const isError = Boolean(refreshError || mutationError);
  const freshness = query.data?.generatedAt
    ? `Updated ${formatAge(query.data.generatedAt)}`
    : 'Waiting for the first automatic refresh';

  return (
    <div ref={heading} tabIndex={-1} aria-label={suggestions ? 'Follow-up suggestions' : 'Next actions'} className={cn('flex flex-col gap-3', className)}>
      <div className="flex min-h-9 flex-wrap items-center justify-between gap-2">
        {!query.isPending && !query.isError ? (
          <div className="text-muted-foreground flex items-center gap-1.5 text-xs" aria-live="polite">
            <ClockIcon className="size-3.5" aria-hidden />
            <span>{query.isFetching ? 'Checking for updates…' : freshness}</span>
          </div>
        ) : (
          <span />
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setRefreshError(null);
            setRefreshMessage(null);
            if (suggestions) void query.refetch();
            else refresh.mutate({ data: audience ? { audience } : {} });
          }}
          disabled={refreshing || busy}
        >
          <RefreshCwIcon className={refreshing ? 'animate-spin' : undefined} aria-hidden />
          {refreshing ? 'Refreshing…' : suggestions ? 'Reload suggestions' : 'Refresh'}
        </Button>
      </div>

      {(notice || feedbackNotice) && (
        <p
          className={cn(
            'text-sm leading-snug break-words',
            isError ? 'text-destructive' : 'text-muted-foreground'
          )}
          role="status"
        >
          {notice ?? feedbackNotice}
        </p>
      )}

      {query.isPending ? (
        <ActionFeedSkeleton />
      ) : query.isError ? (
        <Card>
          <CardContent className="flex flex-col items-start gap-3 p-6">
            <p className="font-medium">Couldn't load next actions</p>
            <p className="text-muted-foreground text-sm break-words">{getErrorMessage(query.error)}</p>
            <Button variant="outline" className="h-11" onClick={() => void query.refetch()}>
              <RefreshCwIcon aria-hidden />
              Try again
            </Button>
          </CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 p-10 text-center">
            <CheckCircle2Icon className="text-muted-foreground size-10" aria-hidden />
            <p className="font-medium">{selectedActionId ? 'This suggestion is no longer available' : "You're all caught up"}</p>
            <p className="text-muted-foreground text-sm">{selectedActionId ? 'It may be resolved, dismissed, snoozed or unavailable in this scope. No action was performed.' : emptyDescription}</p>
          </CardContent>
        </Card>
      ) : (
        items.map((action) => (
          <ActionCard
            key={action.id}
            action={action}
            busy={busy || refreshing}
            showAudienceBadge={showAudienceBadges}
            suggestions={suggestions}
            scopeLabel={scopeLabel}
            selected={action.id === selectedActionId}
            healthLink={linkToHealth ? `/relationship-health?${new URLSearchParams({
              view: 'actions',
              action: action.id,
              ...(action.accountId ? { accountId: action.accountId } : {})
            }).toString()}` : undefined}
            onDismiss={(id) => {
              setMutationError(null);
              dismiss.mutate({ id });
            }}
            onSnooze={(id, hours) => {
              setMutationError(null);
              snooze.mutate({ id, data: { hours } });
            }}
            onApprove={(item) => {
              setMutationError(null);
              if (suggestions) { void openReview(item); return; }
              if (item.actionType === 'email' && item.draft) {
                setSheetError(null);
                setHumanOnlyNotice(null);
                setReviewing(item);
              } else {
                approve.mutate({ id: item.id, data: { requestId: executionRequestId(item.id) } });
              }
            }}
          />
        ))
      )}

      {selectedActionId && <Button variant="outline" asChild><Link to="/relationship-health?view=actions">Back to full review queue</Link></Button>}
      {!query.isError && query.data && <ListPagination label="Follow-up pages" offset={offset} pageSize={pageSize} total={query.data.total} pending={busy || query.isFetching} onChange={value => { setOffset(value); heading.current?.focus(); }} />}
      {reviewing?.draft && (
        <DraftReviewSheet
          key={reviewing.id}
          action={{ ...reviewing, draft: reviewing.draft }}
          sending={approve.isPending}
          error={sheetError}
          humanOnlyMessage={humanOnlyNotice}
          onApprove={({ to, subject, body }) => {
            setSheetError(null);
            approve.mutate({ id: reviewing.id, data: { requestId: executionRequestId(reviewing.id), to, subject, body } });
          }}
          onDismiss={() => {
            setReviewing(null);
            dismiss.mutate({ id: reviewing.id });
          }}
          onClose={() => {
            setReviewing(null);
            setSheetError(null);
            setHumanOnlyNotice(null);
          }}
        />
      )}
    </div>
  );
}
